import React from "react";
import MapView from "./MapView";
import PlaceInfoCard from "./PlaceInfoCard";
import FabGroup from "./FabGroup";
import RouteInfoCard from "./RouteInfoCard";
import NavigationModal from "./NavigationModal";
import PanelBody from "./PanelBody";
import RestaurantInfoCard from "./RestaurantInfoCard";
import NearbyTreasuresLayer from "./NearbyTreasuresLayer";

function MainContent({
  mapKey,
  containerStyle,
  mapCenter,
  onLoad,
  onUnmount,
  mapStyles,
  places,
  routes,
  selectedPlace,
  setSelectedPlace,
  userLat,
  userLng,
  heading,
  directions,
  getPlacePhotoUrl,
  loading,
  shouldShowControls,
  error,
  duration,
  setDuration,
  handleGenerateRoute,
  setShowControls,
  setRoutes,
  setOriginLat,
  setOriginLng,
  setSelectedPlaceMain,
  navStep,
  setNavStep,
  showNavigation,
  setShowNavigation,
  handleSetRoute, // 接收新的統一處理函數
}) {
  // 觸發 POI infocard，並將其設為終點
  const handleSelectPlace = (place) => {
    // 如果選擇了 POI 或自訂地點，則將其經緯度設定為終點
    if (place && place.geometry && place.geometry.location) {
      setSelectedPlaceMain(place); // 更新 App.js 中的 selectedPlace

      // 如果不是自訂地點，則緩存POI信息以便後續終點點擊時使用
      if (!place.isCustom) {
        setLastRoutedPOI(place);
        // 選擇新POI時清除餐廳緩存
        setLastRoutedRestaurant(null);
      }
    } else {
      setSelectedPlaceMain(null); // 清除 App.js 中的 selectedPlace
      // 只有在明確清除選擇時才清除緩存（如設置為null）
      if (place === null) {
        setLastRoutedPOI(null);
        setLastRoutedRestaurant(null);
      }
    }
  };

  // 緩存最近一次 random 餐廳路線的餐廳物件
  const [lastRoutedRestaurant, setLastRoutedRestaurant] = React.useState(null);

  // 緩存最近一次以POI為終點的POI物件
  const [lastRoutedPOI, setLastRoutedPOI] = React.useState(null);

  // 緩存用戶在地圖上選擇的最後一個自定義點
  const [lastCustomPoint, setLastCustomPoint] = React.useState(null);

  // 當 NearbyTreasuresLayer 更新 treasures 時，log 寶藏數量
  const handleTreasuresUpdate = React.useCallback((count) => {
    if (count > 0) {
      console.log(`🪙 附近有 ${count} 個寶藏！`);
    } else {
      console.log("🪙 附近沒有寶藏。");
    }
  }, []);

  // 觸發餐廳 infocard並自動查詢路線
  const handleShowRestaurantWithRoute = (restaurant) => {
    let patched = { ...restaurant };
    if (
      !patched.photo_reference &&
      patched.photos &&
      patched.photos.length > 0
    ) {
      patched.photo_reference = patched.photos[0].photo_reference;
    }
    if (!patched.address) {
      patched.address = patched.vicinity || patched.formatted_address || "";
    }

    // 將餐廳物件設為 selectedPlace，並觸發路線規劃
    const destLatLng = {
      lat: patched.geometry?.location?.lat,
      lng: patched.geometry?.location?.lng,
    };

    if (userLat && userLng && destLatLng.lat && destLatLng.lng) {
      setLastRoutedRestaurant(patched); // 緩存本次路線的餐廳
      // 選擇餐廳時清除POI緩存
      setLastRoutedPOI(null);
      handleSetRoute({
        type: "destination",
        dest_lat: destLatLng.lat,
        dest_lng: destLatLng.lng,
        destination: patched, // 將完整餐廳物件傳遞給 App.js
      });
    } else {
      console.error("User location or destination location is not available.");
    }
  };

  // 關閉 infocard
  const handleCloseInfoCard = () => {
    setSelectedPlaceMain(null); // 直接清除 App.js 的狀態
    console.log("❌ 用戶關閉了信息卡片");
    // 不清除緩存，保留終點信息以便用戶點擊終點時重新顯示
  };

  // 地圖操作時自動收起底部面板
  const handleMapInteraction = () => {
    setShowControls(false);
    // 只有在沒有路線且沒有選擇地點時，地圖互動才清除選擇
    if (routes.length === 0 && !selectedPlace) {
      setSelectedPlaceMain(null); // 清除選擇
    }
  };

  // 讓地圖任意點擊也能觸發選取隨機點，若已選擇POI則收起資訊卡片
  const handleMapClick = (e) => {
    if (selectedPlace) {
      setSelectedPlaceMain(null);
      // 不清除POI緩存，保留終點信息以便用戶點擊終點時重新顯示
      return;
    }

    // 取得經緯度
    const lat =
      typeof e.latLng.lat === "function" ? e.latLng.lat() : e.latLng.lat;
    const lng =
      typeof e.latLng.lng === "function" ? e.latLng.lng() : e.latLng.lng;

    // 用戶選擇新的自定義地點時，清除之前的緩存
    setLastRoutedPOI(null);
    setLastRoutedRestaurant(null);

    // 構造一個自定義地點物件
    const customPlace = {
      name: "自定義位置",
      geometry: {
        location: {
          lat,
          lng,
        },
      },
      isCustom: true,
      timestamp: new Date().toISOString(), // 添加時間戳
    };

    // 緩存這個自定義點
    setLastCustomPoint(customPlace);
    console.log("🎯 用戶選擇自定義點:", {
      coordinates: { lat, lng },
      timestamp: customPlace.timestamp,
      place: customPlace,
    });

    // 只設為選擇的地點，不自動生成路線
    setSelectedPlaceMain(customPlace);
    console.log("� 已標記位置，等待用戶手動生成路線");
  };

  // 根據 selectedPlace 決定顯示哪個 infocard
  const infoCard = React.useMemo(() => {
    if (!selectedPlace || selectedPlace.isCustom) {
      return null;
    }
    // 判斷是餐廳還是 POI
    const isRestaurant =
      selectedPlace.types?.includes("restaurant") ||
      selectedPlace.types?.includes("food");
    if (isRestaurant) {
      return {
        type: "restaurant",
        data: selectedPlace,
      };
    }
    return {
      type: "poi",
      data: { place: selectedPlace, position: null },
    };
  }, [selectedPlace]);

  // 當控制面板顯示時自動隱藏 infocard
  React.useEffect(() => {
    // 註解掉自動清除選擇的邏輯，讓POI資訊在面板顯示時保持顯示
    // if (shouldShowControls) {
    //   setSelectedPlaceMain(null);
    // }
  }, [shouldShowControls, setSelectedPlaceMain]);

  // 監聽並記錄最後選擇的自定義點變化
  React.useEffect(() => {
    if (lastCustomPoint) {
      console.log("📍 最後選擇的自定義點已更新:", {
        name: lastCustomPoint.name,
        coordinates: {
          lat: lastCustomPoint.geometry.location.lat,
          lng: lastCustomPoint.geometry.location.lng,
        },
        timestamp: lastCustomPoint.timestamp,
        cached: true,
      });
    }
  }, [lastCustomPoint]);

  // 地圖終點 marker 點擊時，若終點是餐廳，重新顯示餐廳 infocard
  const handleMapEndMarkerClick = () => {
    if (routes.length > 0 && routes[0].path && routes[0].path.length > 0) {
      const endLocation = routes[0].path[routes[0].path.length - 1];

      // 優先用緩存的餐廳物件
      if (lastRoutedRestaurant) {
        setSelectedPlaceMain(lastRoutedRestaurant);
        return;
      }

      // 其次用緩存的POI物件
      if (lastRoutedPOI) {
        setSelectedPlaceMain(lastRoutedPOI);
        return;
      }

      // 檢查 infoCard 是否為餐廳
      if (infoCard?.type === "restaurant") {
        setSelectedPlaceMain(infoCard.data);
      } else {
        // 嘗試從 places 找出與終點經緯度相符的地點（允許10公尺誤差）
        const elat =
          typeof endLocation.lat === "function"
            ? endLocation.lat()
            : endLocation.lat;
        const elng =
          typeof endLocation.lng === "function"
            ? endLocation.lng()
            : endLocation.lng;
        const EARTH_RADIUS = 6371000; // meters
        function haversine(lat1, lng1, lat2, lng2) {
          const toRad = (d) => (d * Math.PI) / 180;
          const dLat = toRad(lat2 - lat1);
          const dLng = toRad(lng2 - lng1);
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) *
              Math.cos(toRad(lat2)) *
              Math.sin(dLng / 2) *
              Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return EARTH_RADIUS * c;
        }
        const match = places.find((p) => {
          const plat =
            typeof p.geometry?.location?.lat === "function"
              ? p.geometry.location.lat()
              : p.geometry?.location?.lat;
          const plng =
            typeof p.geometry?.location?.lng === "function"
              ? p.geometry.location.lng()
              : p.geometry?.location?.lng;
          const dist = haversine(plat, plng, elat, elng);

          // 檢查距離匹配（10公尺內）
          if (dist < 10) {
            return true;
          }
          // 若名稱也有 match（備用）
          if (
            p.name &&
            routes[0].destination_name &&
            p.name === routes[0].destination_name
          ) {
            return true;
          }
          return false;
        });
        if (match) {
          setSelectedPlaceMain(match);
        }
      }
    }
  };

  return (
    <div className="relative w-full h-full bg-[#101217]">
      {/* 路線資訊卡片 */}
      {routes.length > 0 &&
        routes[0].total_distance &&
        routes[0].total_duration &&
        !selectedPlace && ( // 只有未選擇 POI 時才顯示 RouteInfoCard
          <RouteInfoCard
            distance={routes[0].total_distance
              .replace(" km", "公里")
              .replace(" m", "米")}
            duration={routes[0].total_duration
              .replace(" hours", "小時")
              .replace(" hour", "小時")
              .replace(" minutes", "分鐘")
              .replace(" minute", "分鐘")
              .replace(" mins", "分鐘")
              .replace(" min", "分鐘")}
            steps={
              Math.round((parseFloat(routes[0].total_distance) * 1400) / 10) *
              10
            }
          />
        )}
      {/* 地圖 */}
      <MapView
        mapKey={mapKey}
        containerStyle={containerStyle}
        mapCenter={mapCenter}
        onLoad={onLoad}
        onUnmount={onUnmount}
        mapStyles={mapStyles}
        places={places}
        routes={routes}
        directions={directions}
        selectedPlace={selectedPlace}
        setSelectedPlace={handleSelectPlace}
        userLat={userLat}
        userLng={userLng}
        heading={heading}
        handleMapClick={handleMapClick}
        onMapInteraction={handleMapInteraction}
        onEndMarkerClick={handleMapEndMarkerClick}
      >
        {/* 新增：附近寶藏 Layer */}
        <NearbyTreasuresLayer
          userLat={userLat}
          userLng={userLng}
          onNearbyTreasuresChange={handleTreasuresUpdate}
        />
      </MapView>
      {/* infocard 統一渲染 */}
      <div
        className="fixed bottom-24 left-8 z-[1020] h-auto"
        style={{ minWidth: 0 }}
      >
        <RestaurantInfoCard
          name={
            infoCard?.type === "restaurant" ? infoCard.data.name : undefined
          }
          address={
            infoCard?.type === "restaurant"
              ? infoCard.data.vicinity || infoCard.data.address
              : undefined
          }
          phone={
            infoCard?.type === "restaurant" ? infoCard.data.phone : undefined
          }
          imageUrl={
            infoCard?.type === "restaurant" ? infoCard.data.imageUrl : undefined
          }
          photoUrl={
            infoCard?.type === "restaurant" ? infoCard.data.photoUrl : undefined
          }
          photo_reference={
            infoCard?.type === "restaurant"
              ? infoCard.data.photo_reference ||
                (infoCard.data.photos &&
                  infoCard.data.photos[0]?.photo_reference)
              : undefined
          }
          rating={
            infoCard?.type === "restaurant" ? infoCard.data.rating : undefined
          }
          user_ratings_total={
            infoCard?.type === "restaurant"
              ? infoCard.data.user_ratings_total
              : undefined
          }
          distance={
            infoCard?.type === "restaurant" ? infoCard.data.distance : undefined
          }
          distance_meters={
            infoCard?.type === "restaurant"
              ? infoCard.data.distance_meters
              : undefined
          }
          distance_km={
            infoCard?.type === "restaurant"
              ? infoCard.data.distance_km
              : undefined
          }
          description={
            infoCard?.type === "restaurant"
              ? infoCard.data.description
              : undefined
          }
          onClose={handleCloseInfoCard}
          animateClass={undefined}
          show={infoCard?.type === "restaurant"}
        />
      </div>
      <PlaceInfoCard
        selectedPlace={infoCard?.type === "poi" ? infoCard.data.place : null}
        getPlacePhotoUrl={getPlacePhotoUrl}
        onClose={handleCloseInfoCard}
        position={infoCard?.type === "poi" ? infoCard.data.position : null}
        show={infoCard?.type === "poi"}
      />
      {/* 底部控制面板 */}
      <PanelBody
        loading={loading}
        shouldShowControls={shouldShowControls}
        error={error}
        duration={duration}
        setDuration={setDuration}
        selectedPlace={selectedPlace}
        handleGenerateRoute={handleGenerateRoute} // This can be removed if no longer used directly
        handleSetRoute={handleSetRoute} // Pass down the new function
        setShowControls={setShowControls}
        setSelectedPlace={setSelectedPlace}
        places={places} // 傳遞 places 給 PanelBody，讓其能正確判斷 POI
      />
      {/* 右下角 + 按鈕，僅在控制面板隱藏且非 loading 狀態時顯示 */}
      {/* <ShowPanelButton
        shouldShowControls={shouldShowControls}
        loading={loading}
        setShowControls={setShowControls}
      /> */}
      {/* 浮動按鈕群組 */}
      <FabGroup
        setShowControls={setShowControls}
        routes={routes}
        shouldShowControls={shouldShowControls}
        setRandomRestaurant={handleShowRestaurantWithRoute} // 修正：傳遞 handleShowRestaurantWithRoute
      />
      {/* 導航步驟彈窗 */}
      <NavigationModal
        open={showNavigation}
        steps={routes[0]?.steps || []}
        currentStep={navStep}
        onClose={() => setShowNavigation(false)}
        onPrev={() => setNavStep((s) => Math.max(0, s - 1))}
        onNext={() =>
          setNavStep((s) =>
            Math.min((routes[0]?.steps?.length || 1) - 1, s + 1)
          )
        }
      />
    </div>
  );
}

export default MainContent;
